import { useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES, getCitiesForCountry, hasCityList } from "../lib/worldCities";
import { Input } from "./ui/input";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Country + City picker with "Other" fallback.
 *
 * Props
 *   country, city — controlled values (strings)
 *   onChange({country, city}) — fires on any change
 *
 * Behaviour
 *   - Country dropdown lists the full standard country set (Pakistan first).
 *   - When the user selects a country with a curated city list, the city
 *     dropdown becomes searchable with "+ Other (city not in list)" pinned
 *     at the TOP of the list. Picking Other reveals a free-text input.
 *   - When the country has no curated city list (e.g. Niger), the city
 *     field is a plain free-text input so users are never blocked.
 *   - Changing the country resets the city automatically.
 */
export default function CountryCitySelect({ country, city, onChange,
                                            countryTestId = "country-select",
                                            cityTestId = "city-select",
                                            className = "" }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${className}`}>
      <CountryDropdown
        value={country}
        onChange={(c) => onChange({ country: c, city: "" })}
        testid={countryTestId}
      />
      {hasCityList(country) ? (
        <CityDropdown
          country={country}
          value={city}
          onChange={(v) => onChange({ country, city: v })}
          testid={cityTestId}
        />
      ) : (
        <Input
          data-testid={cityTestId}
          value={city || ""}
          onChange={(e) => onChange({ country, city: e.target.value })}
          placeholder={country ? "Type your city / town" : "Pick a country first"}
          disabled={!country}
          className="rounded-none"
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
function CountryDropdown({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.toLowerCase().includes(q));
  }, [search]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 border-2 border-neutral-300 hover:border-black focus:outline-none focus:border-black bg-white flex items-center justify-between text-sm"
      >
        <span className={value ? "text-black" : "text-neutral-400"}>{value || "Select country"}</span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border-2 border-black shadow-lg max-h-[420px] overflow-hidden flex flex-col" data-testid={`${testid}-panel`}>
          <div className="p-2 border-b border-neutral-200 flex items-center gap-2 bg-neutral-50">
            <MagnifyingGlass size={14} className="text-neutral-400" />
            <input
              autoFocus
              data-testid={`${testid}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries..."
              className="flex-1 bg-transparent outline-none text-sm"
            />
            {search && <button type="button" onClick={() => setSearch("")} className="text-neutral-400 hover:text-black"><X size={14} /></button>}
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-neutral-500">No country matches "{search}".</div>
            ) : filtered.map((c) => (
              <button
                type="button"
                key={c}
                data-testid={`${testid}-opt-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                onClick={() => { onChange(c); setSearch(""); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-mango/10 hover:text-black ${value === c ? "bg-mango/20 font-bold" : ""}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
function CityDropdown({ country, value, onChange, testid }) {
  const cities = useMemo(() => getCitiesForCountry(country), [country]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [otherMode, setOtherMode] = useState(false);
  const [otherText, setOtherText] = useState("");
  const ref = useRef(null);

  const isOther = !!value && !cities.includes(value);

  useEffect(() => {
    if (isOther) {
      setOtherText(value);
      setOtherMode(true);
    } else {
      setOtherMode(false);
      setOtherText("");
    }
  }, [isOther, value, country]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => c.toLowerCase().includes(q));
  }, [search, cities]);

  const display = value ? (isOther ? `${value} (other)` : value) : "";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 border-2 border-neutral-300 hover:border-black focus:outline-none focus:border-black bg-white flex items-center justify-between text-sm"
      >
        <span className={display ? "text-black" : "text-neutral-400"}>{display || `Select city in ${country}`}</span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border-2 border-black shadow-lg max-h-[420px] overflow-hidden flex flex-col" data-testid={`${testid}-panel`}>
          {!otherMode && (
            <div className="p-2 border-b border-neutral-200 flex items-center gap-2 bg-neutral-50">
              <MagnifyingGlass size={14} className="text-neutral-400" />
              <input
                autoFocus
                data-testid={`${testid}-search`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search cities in ${country}...`}
                className="flex-1 bg-transparent outline-none text-sm"
              />
              {search && <button type="button" onClick={() => setSearch("")} className="text-neutral-400 hover:text-black"><X size={14} /></button>}
            </div>
          )}
          {/* Other pinned at TOP */}
          {!otherMode && (
            <div className="border-b-2 border-black bg-mango/5">
              <button
                type="button"
                onClick={() => setOtherMode(true)}
                className="w-full text-left px-3 py-2.5 text-sm font-bold hover:bg-mango/10"
                data-testid={`${testid}-other-trigger`}
              >
                + Other (city not in list)
              </button>
            </div>
          )}
          {!otherMode && (
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-neutral-500">No matches. Tap "Other" above to type your city.</div>
              ) : filtered.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`${testid}-opt-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  onClick={() => { onChange(c); setSearch(""); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-mango/10 hover:text-black ${value === c ? "bg-mango/20 font-bold" : ""}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          {otherMode && (
            <div className="p-3 bg-neutral-50 border-t-2 border-black space-y-2" data-testid={`${testid}-other`}>
              <div className="flex items-center justify-between">
                <div className="overline">YOUR CITY</div>
                <button
                  type="button"
                  onClick={() => { setOtherMode(false); setOtherText(""); onChange(""); }}
                  className="text-xs text-neutral-500 hover:text-black underline"
                >
                  ← back to list
                </button>
              </div>
              <Input
                autoFocus
                data-testid={`${testid}-other-input`}
                value={otherText}
                onChange={(e) => { setOtherText(e.target.value); onChange(e.target.value); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); } }}
                placeholder="Type your city name"
                className="rounded-none text-sm"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!otherText.trim()}
                  onClick={() => setOpen(false)}
                  className="px-3 py-1.5 text-xs bg-black text-white hover:bg-neutral-800 disabled:opacity-50"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
